import { useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { customerMethods } from "../../api/apiMethods";
import { genericErrorConfig } from "../../common/exceptions";
import { MatkontoVerifiedCodes } from "../../common/helpers/matkontoTmpInfoProider";
import Container from "../../components/Container";
import Loader from "../../components/Loader";
import { ModalContext } from "../../components/Modal";
import Plate from "../../components/Plate";
import SubHeader from "../../components/SubHeader";
import useSelectedMunicipalityId from "../../hooks/useSelectedMunicipalityId";
import { queryKeys } from "../../queries/queryKeys";
import { routes } from "../../routes";
import { Logger } from "../../utils/logger/Logger";

/** Survives Strict Mode remounts; prevents duplicate verify calls for the same code. */
const verifyingCodes = new Set<string>();

const MatkontoCallbackView = () => {
    const municipalityId = useSelectedMunicipalityId();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setModalOptions } = useContext(ModalContext);

    useEffect(() => {
        const matkontoVerification = async () => {
            const transactionCode = searchParams.get("code");
            const unitId = searchParams.get("unitId");
            const customerId = searchParams.get("customerId");

            if (!transactionCode || !unitId || !customerId) {
                Logger.error(
                    new Error("Matkonto - Missing required query parameters")
                );
                setModalOptions(
                    genericErrorConfig(
                        () => setModalOptions({ visible: false }),
                        "Kunde inte verify Matkonto."
                    )
                );
                return;
            }

            // Re-entry (e.g. browser Back): do not call the provider again
            if (MatkontoVerifiedCodes.has(transactionCode)) {
                navigate(
                    routes.customerById(municipalityId, customerId),
                    { replace: true }
                );
                return;
            }

            if (verifyingCodes.has(transactionCode)) {
                return;
            }
            verifyingCodes.add(transactionCode);

            try {
                const response = await customerMethods(
                    municipalityId
                ).verifyMatkonto(unitId, customerId, transactionCode);

                if (response) {
                    MatkontoVerifiedCodes.add(transactionCode);
                    await queryClient.invalidateQueries({
                        queryKey: queryKeys.customer.byId(
                            customerId,
                            municipalityId
                        ),
                    });
                    navigate(
                        routes.customerById(municipalityId, customerId),
                        {
                            replace: true,
                            state: { matkontoAdded: true },
                        }
                    );
                } else {
                    verifyingCodes.delete(transactionCode);
                }
            } catch (error: any) {
                verifyingCodes.delete(transactionCode);
                Logger.error(error);
                setModalOptions(
                    genericErrorConfig(
                        () => setModalOptions({ visible: false }),
                        "Kunde inte verify Matkonto."
                    )
                );
            }
        };

        matkontoVerification();
    }, [
        municipalityId,
        searchParams,
        setModalOptions,
        navigate,
        queryClient,
    ]);

    return (
        <Container>
            <SubHeader title="Kunde inte verifiera Matkonto." />
            <Plate>
                <Loader />
            </Plate>
        </Container>
    );
};

export default MatkontoCallbackView;
